import { memo, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Dropdown, Space, Tag } from 'antd';
import _ from 'lodash';
import { dateToString, getFullName } from '@/utils';
import { useGetParentChildInquiriesQuery, useGetParentRecommendationQuery } from '@/services/parent-api';
import { CaretDownSvg } from '@/assets/icons';
import Table from '@/components/table';
import Button from '@/components/button';
import AddModal from './add-modal';
import LettersModal from './letters-modal';
import CompleteModal from './complete-modal';
import MessageModal from './message-modal';

const ChildInquiries = ({ parentId, permissions }) => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const pageNumber = queryParams.get('page') || 1;
  const { data, isFetching, refetch } = useGetParentChildInquiriesQuery({ parentId, pageNumber });
  const { data: data2, isLoading: loading2 } = useGetParentRecommendationQuery({ parentId });
  const [event, setEvent] = useState(false);

  const items = useMemo(
    () => data?.data.map((item) => ({ ..._.omit(item, ['children']), children_data: item.children })),
    [data],
  );

  const [isAddModalOpen, openAddModal] = useState(false);
  const [isMsgModalOpen, openMsgModal] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedId1, setSelectedId1] = useState();
  const [selectedId2, setSelectedId2] = useState();

  useEffect(() => {
    if (selectedChild) {
      openMsgModal(true);
    }
  }, [selectedChild]);

  const handleAddModal = (type) => {
    setEvent(type);
    openAddModal(true);
  };

  const columns = [
    {
      title: 'Inquiry Date',
      dataIndex: 'date',
      render: (value, record) => (
        <Space direction="vertical" size={2}>
          {dateToString(value)}
          {record.fromPortal && <Tag color="processing">Portal Inquiry</Tag>}
        </Space>
      ),
    },
    {
      title: 'Child',
      dataIndex: 'children_data',
      render: (value) => (
        <Space direction="vertical" size={0}>
          {value.map((item) => (
            <Link key={item.childId} to={`/child/${item.childId}`}>
              {getFullName(item.name)}
            </Link>
          ))}
        </Space>
      ),
    },
    {
      title: 'Letters',
      dataIndex: 'letters',
      render: (value) => value.length,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) =>
        [
          record.inquiryStatus.name,
          record.completedOn && `on ${dateToString(record.completedOn)}`,
          record.completedReason.name,
        ]
          .filter((s) => s)
          .join(' '),
    },
    {
      title:
        permissions.events &&
        (data2 ? (
          <Dropdown
            menu={{
              items: [
                {
                  key: '1',
                  label: 'Child Inquiry',
                  onClick: () => {
                    handleAddModal(false);
                  },
                },
                {
                  key: '2',
                  label: 'Meet & Greet Inquiry',
                  onClick: () => {
                    handleAddModal(true);
                  },
                },
              ],
            }}
          >
            <Button type="primary">
              Add <CaretDownSvg fontSize="13" style={{ marginLeft: 6 }} />
            </Button>
          </Dropdown>
        ) : (
          <Button type="primary" onClick={() => handleAddModal(false)}>
            Add
          </Button>
        )),
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Space size="small">
          {permissions.writeChild &&
            (record.isOpen ? (
              <Button type="link" onClick={() => setSelectedId1(record.inquiryId)}>
                Complete
              </Button>
            ) : (
              <span style={{ opacity: 0.5 }}>Completed</span>
            ))}
          <Button type="link" onClick={() => setSelectedId2(record.inquiryId)}>
            Letters
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={items}
        rowKey="inquiryId"
        loading={isFetching || loading2}
        pageInfo={data}
        size="small"
      />

      <AddModal
        parentId={parentId}
        open={isAddModalOpen}
        onAdd={refetch}
        isRecommended={event}
        setSelectedChild={setSelectedChild}
        onClose={() => openAddModal(false)}
      />
      <CompleteModal parentId={parentId} inquiryId={selectedId1} refetch={refetch} onClose={() => setSelectedId1()} />
      <LettersModal parentId={parentId} inquiryId={selectedId2} onClose={() => setSelectedId2()} />
      <MessageModal
        parentId={parentId}
        open={isMsgModalOpen}
        selectedChild={selectedChild}
        isRec={event}
        refetch={refetch}
        onClose={() => openMsgModal(false)}
      />
    </>
  );
};

export default memo(ChildInquiries);
